import axios from 'axios';
import {Store} from 'vuex';
import VueRouter from 'vue-router';
import TranslationService from '@/locale/translation.service';
import {AccountState} from '@/shared/model/account-state.model';
import moment from 'moment';

export default class AccountService {
  constructor(private store: Store<any>, private translationService: TranslationService, private cookie: any, private router: VueRouter) {
    this.init();
  }

  public init(): void {
    this.retrieveProfiles();
  }

  public getBranchLevelCode(): number {
    let branchCode = null;
    if (this.store.getters.account && this.store.getters.account.branchDTO) {
      branchCode = this.store.getters.account.branchDTO.levelCode;
    }
    return branchCode;
  }

  public getJobId(): any {
    const {currentJobId, jobId} = this.store.getters.account;
    return currentJobId ? currentJobId : jobId;
  }

  public getAccount(): any {
    return this.store.getters.account;
  }

  public getStructureId(): any {
    return this.store.getters.account.structureId;
  }

  public getBranchId(): any {
    return this.store.getters.account.employeeBranchId;
  }

  public getBranchName(): any {
    return this.store.getters.account.employeeBranchName;
  }

  public get employeePinfl(): string {
    return this.store.getters.account.pinfl;
  }

  public getBranch(): any {
    return this.store.getters.account.branchDTO;
  }

  public retrieveProfiles(): Promise<boolean> {
    return new Promise(resolve => {
      axios
        .get<any>('management/info')
        .then(res => {
          if (res.data && res.data.activeProfiles) {
            this.store.commit('setRibbonOnProfiles', res.data['display-ribbon-on-profiles']);
            this.store.commit('setActiveProfiles', res.data['activeProfiles']);
          }
          resolve(true);
        })
        .catch(() => resolve(false));
    });
  }

  public retrieveAccount(): Promise<boolean> {
    return new Promise(resolve => {
      axios
        .get<any>('api/account')
        .then(response => {
          this.store.commit('authenticate');
          const account = response.data;
          if (account) {
            this.store.commit('authenticated', account);
            if (this.store.getters.currentLanguage !== account.langKey) {
              this.store.commit('currentLanguage', account.langKey);
            }
            if (sessionStorage.getItem('requested-url')) {
              this.router.replace(sessionStorage.getItem('requested-url'));
              sessionStorage.removeItem('requested-url');
            }
          } else {
            this.store.commit('logout');
            if (this.router.currentRoute.path !== '/') {
              this.router.push('/');
            }
            sessionStorage.removeItem('requested-url');
          }
          this.translationService.refreshTranslation(this.store.getters.currentLanguage);
          resolve(true);
        })
        .catch(() => {
          this.store.commit('logout');
          resolve(false);
        });
    });
  }

  public getHealthCheck(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      axios
        .get(`api/hr-structures/health-check`)
        .then(res => {
          resolve(res);
        })
        .catch(err => {
          reject(err);
        });
    });
  }


  public retrieveAccountState(): Promise<AccountState> {
    return new Promise(resolve => {
      axios
        .get<any>('api/account')
        .then(response => {
          this.store.commit('authenticate');
          const account = response.data;
          let accountState = new AccountState();
          if (account) {
            accountState.id = account['responseStatus'];
            accountState.message = account['responseMessage'];
            this.store.commit('authenticated', account);
            if (this.store.getters.currentLanguage !== account.langKey) {
              this.store.commit('currentLanguage', account.langKey);
            }
            if (sessionStorage.getItem('requested-url')) {
              this.router.replace(sessionStorage.getItem('requested-url'));
              sessionStorage.removeItem('requested-url');
            }
          } else {
            accountState.id = 100;
            this.store.commit('logout');
            if (this.router.currentRoute.path !== '/') {
              this.router.push('/');
            }
            sessionStorage.removeItem('requested-url');
          }
          this.translationService.refreshTranslation(this.store.getters.currentLanguage);
          resolve(accountState);
        })
        .catch(error => {
          let accountState = new AccountState();
          let errorStatus = error.response.status;
          accountState.id = error.response.status;
          accountState.message = error.response;
          switch (errorStatus) {
            case 401:
              accountState.id = 401;
              break;
            case 500:
              accountState.id = 500;
              accountState.message = error.response;
              break;
          }
          this.store.commit('logout');
          resolve(accountState);
        });
    });
  }

  public hasAnyAuthorityAndCheckAuth(authorities: any): Promise<boolean> {
    if (typeof authorities === 'string') {
      authorities = [authorities];
    }

    if (!this.authenticated || !this.userAuthorities) {
      const token = this.cookie.get('JSESSIONID') || this.cookie.get('XSRF-TOKEN');
      if (!this.store.getters.account && !this.store.getters.logon && token) {
        return this.retrieveAccount().then(resp => {
          if (resp) {
            return this.checkAuthorities(authorities);
          }
          return Promise.resolve(false);
        });
      }
      return Promise.resolve(false);
    }
    return this.checkAuthorities(authorities);
  }

  public get authenticated(): boolean {
    return this.store.getters.authenticated;
  }

  public get userAuthorities(): any {
    return this.store.getters.account?.authorities;
  }

  private checkAuthorities(authorities: any): Promise<boolean> {
    if (this.userAuthorities) {
      for (const authority of authorities) {
        if (this.userAuthorities.includes(authority)) {
          return Promise.resolve(true);
        }
      }
    }
    return Promise.resolve(false);
  }

  public get currentLanguage(): any {
    return this.store.getters.currentLanguage;
  }

  public get languages(): any {
    return this.store.getters.languages;
  }

  public get currentLanguageCode(): string {
    const currentLanguageKey = this.currentLanguage;
    let currentLanguage = 'nameUzCrl';
    if (currentLanguageKey === 'uz-Cyrl-uz') {
      currentLanguage = 'nameUzCrl';
    }
    if (currentLanguageKey === 'uz-Latn-uz') {
      currentLanguage = 'nameUzLat';
    }
    if (currentLanguageKey === 'en') {
      currentLanguage = 'nameEn';
    }
    if (currentLanguageKey === 'ru') {
      currentLanguage = 'nameRu';
    }
    return currentLanguage;
  }

  public onlyOneRole(inRoleCode: string): boolean {
    let result = false;
    if (!this.store.getters.authenticated || !this.store.getters.account) {
      return false;
    }
    if (this.store.getters.account.employeeRoleDTOList && this.store.getters.account.employeeRoleDTOList.length == 1) {
      let roleCode = this.store.getters.account.employeeRoleDTOList[0].roleCode;
      if (roleCode === inRoleCode) {
        result = true;
      }
    }
    return result;
  }

  public isSuperPerson(): boolean {
    let result = false;
    let account = this.store.getters.account;
    if (account) {
      if (account['login'] === 'azizbek' || account['login'] === 'boqiyevich.o'|| account['login'] === 'system') {
        result = true;
      }
    }
    return result;
  }

  public inRole(inRoleCode: string): boolean {
    let result = false;
    if (!this.store.getters.authenticated || !this.store.getters.account) {
      return false;
    }
    let account = this.store.getters.account;
    if (account) {
      if (account['login'] === 'azizbek' || account['login'] === 'boqiyevich.o'|| account['login'] === 'system') {
        return true;
      }
      if (account.jobRoleList && account.jobRoleList.length > 0) {
        result = account.jobRoleList.includes(inRoleCode);
      }
    }
    return result;
  }

  public isHasRole(rolePermission): boolean {
    let result = false;
    const account = this.getAccount();
    if (account) {
      if (account['login'] === 'azizbek' || account['login'] === 'boqiyevich.o'|| account['login'] === 'system') {
        return true;
      }
      if (account.jobRoleList && account.jobRoleList.length > 0) {
        result = account.jobRoleList.includes(rolePermission);
      }
    }
    return result;
  }

  public get currentUpperLanguageCode(): string {
    const currentLanguageKey = this.currentLanguage;
    let currentLanguage = 'NameUzCrl';
    if (currentLanguageKey === 'uz-Cyrl-uz') {
      currentLanguage = 'NameUzCrl';
    }
    if (currentLanguageKey === 'uz-Latn-uz') {
      currentLanguage = 'NameUzLat';
    }
    if (currentLanguageKey === 'en') {
      currentLanguage = 'NameEn';
    }
    if (currentLanguageKey === 'ru') {
      currentLanguage = 'NameRu';
    }
    return currentLanguage;
  }

  public get deviceType(): string {
    let w_width = window.innerWidth;
    let deviceType;
    if (w_width >= 1600) {
      deviceType = 'DESKTOP-WIDE';
    } else if (w_width > 991) {
      deviceType = 'DESKTOP';
    } else if (w_width >= 768) {
      deviceType = 'TABLET';
    } else {
      deviceType = 'MOBILE';
    }
    return deviceType;
  }

  public col_col_col_12(deviceType): string {
    let col = 'col';
    switch (deviceType) {
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_2_4_12(deviceType): string {
    let col = 'col-2';
    switch (deviceType) {
      case 'DESKTOP':
        col = 'col-4';
        break;
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_2_6_12(deviceType): string {
    let col = 'col-2';
    switch (deviceType) {
      case 'DESKTOP':
        col = 'col-6';
        break;
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_4_4_6(deviceType): string {
    let col = 'col-4';
    switch (deviceType) {
      case 'DESKTOP':
        col = 'col-4';
        break;
      case 'TABLET':
      case 'MOBILE':
        col = 'col-6';
        break;
    }
    return col;
  }

  public col_2_2_6(deviceType): string {
    let col = 'col-2';
    switch (deviceType) {
      case 'DESKTOP':
        col = 'col-2';
        break;
      case 'TABLET':
      case 'MOBILE':
        col = 'col-6';
        break;
    }
    return col;
  }

  public col_3_6_12(deviceType): string {
    let col = 'col-3';
    switch (deviceType) {
      case 'DESKTOP-WIDE':
        col = 'col-3';
        break;
      case 'DESKTOP':
        col = 'col-6';
        break;
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_3_12_12(deviceType): string {
    let col = 'col-3';
    switch (deviceType) {
      case 'DESKTOP-WIDE':
        col = 'col-3';
        break;
      case 'DESKTOP':
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_3_3_12(deviceType): string {
    let col = 'col-3';
    switch (deviceType) {
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_3_4_12(deviceType): string {
    let col = 'col-3';
    switch (deviceType) {
      case 'DESKTOP':
        col = 'col-4';
        break;
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_4_4_12(deviceType): string {
    let col = 'col-4';
    switch (deviceType) {
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_4_6_12(deviceType): string {
    let col = 'col-4';
    switch (deviceType) {
      case 'DESKTOP':
        col = 'col-6';
        break;
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_4_12_12(deviceType): string {
    let col = 'col-4';
    switch (deviceType) {
      case 'DESKTOP':
        col = 'col-12';
        break;
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_6_4_12(deviceType): string {
    let col = 'col-6';
    switch (deviceType) {
      case 'DESKTOP':
        col = 'col-4';
        break;
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_6_6_12(deviceType): string {
    let col = 'col-6';
    switch (deviceType) {
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_6_12_12(deviceType): string {
    let col = 'col-6';
    switch (deviceType) {
      case 'DESKTOP':
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_6_9_12(deviceType): string {
    let col = 'col-6';
    switch (deviceType) {
      case 'DESKTOP':
        col = 'col-9';
        break;
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_9_6_12(deviceType): string {
    let col = 'col-12';
    switch (deviceType) {
      case 'DESKTOP-WIDE':
        col = 'col-9';
        break;
      case 'DESKTOP':
        col = 'col-6';
        break;
    }
    return col;
  }

  public col_9_9_12(deviceType): string {
    let col = 'col-9';
    switch (deviceType) {
      case 'TABLET':
      case 'MOBILE':
        col = 'col-12';
        break;
    }
    return col;
  }

  public col_12_12_12(): string {
    return 'col-12';
  }

  public getAvatarImage(avatarFileId): string {
    if (!avatarFileId) {
      return '@/../content/images/empty_avatar.png';
    }
    if (avatarFileId) {
      if (avatarFileId.search('http://hr.adliya.uz') != -1) {
        return `api/file-storages/employee-avatar?url=${avatarFileId}`;
      } else return `api/file-storages/preview/${avatarFileId}`;
    }
  }

  public getLogoImage(fileName): string {
    if (!fileName) {
      return '@/../content/images/doc-logo/uzbekistan.svg';
    }
    if (fileName) {
      return `@/../content/images/doc-logo/${fileName}`;
    }
  }

  public getEmployeeShortFirstName(row, key): string {
    let shortName = '';
    if (row) {
      if (row[key]) {
        shortName = row[key]['firstName'].substring(0, 1) + '.';
        shortName += row[key]['middleName'].substring(0, 1) + '. ';
        shortName += row[key]['lastName'];
      }
    }
    return shortName;
  }

  public getEmployeeShortNamePosition(row, key): string {
    let shortName = '';
    if (row) {
      if (row[key]) {
        shortName = row[key]['firstName'] + ' ';
        shortName += row[key]['middleName'].substring(0, 1) + '.';
        shortName += row[key]['lastName'].substring(0, 1);
        shortName += ' - ' + row[key]['positionName'];
      }
    }
    return shortName;
  }

  public checkValidate(fieldValue: any, fieldName: string): boolean {
    let valid = true;
    switch (fieldName) {
      default:
        if (!(fieldValue && fieldValue !== '' && fieldValue != null)) {
          valid = false;
        }
        if (Array.isArray(fieldValue)) {
          if (fieldValue.length === 0) {
            valid = false;
            return valid;
          }
        }
        break;
    }
    return valid;
  }

  public calcDeadline(deadlineDateStr): any {
    let calc = {
      key: null,
      day: null,
    };
    if (deadlineDateStr) {
      let deadlineDate = moment(deadlineDateStr, 'DD.MM.YYYY');
      let today = moment(moment().format('DD.MM.YYYY'), 'DD.MM.YYYY');
      if (deadlineDate.diff) {
        let diffDay = moment.duration(deadlineDate.diff(today)).asDays();
        if (diffDay != null) {
          calc.day = diffDay;
          if (diffDay < 0) {
            calc.key = 'BEFORE';
            calc.day = -1 * diffDay;
          } else if (diffDay === 0) {
            calc.key = 'TODAY';
          } else if (diffDay > 0) {
            calc.key = 'AFTER';
          }
        }
      }
    }
    return calc;
  }
}
